/** Non-component helpers for the member design kit (kept apart for fast refresh). */
import type { Variants } from "motion/react";

import { cn } from "@/lib/utils/cn";

export const EASE = [0.22, 1, 0.36, 1] as const;

export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.42, ease: EASE } },
};

export const surfaceClass =
  "rounded-[20px] border border-border-100 bg-surface shadow-[0_1px_2px_rgba(15,46,38,0.04),0_4px_16px_rgba(15,46,38,0.04)] " +
  "dark:shadow-[0_1px_2px_rgba(0,0,0,0.3)]";

type BtnVariant = "primary" | "secondary" | "ghost" | "danger";

export function pillButtonClass(variant: BtnVariant = "primary", size: "sm" | "md" | "lg" = "md") {
  const sizes = { sm: "h-9 px-3.5 text-sm", md: "h-11 px-5 text-sm", lg: "h-12 px-6 text-[15px]" };
  const variants: Record<BtnVariant, string> = {
    primary:
      "bg-primary-600 text-white shadow-[0_1px_2px_rgba(0,0,0,0.12),0_4px_12px_rgb(var(--color-primary-600)/0.28)] hover:bg-primary-700",
    secondary:
      "border border-border-200 bg-surface text-ink-900 hover:bg-ink-900/[0.03] dark:hover:bg-white/[0.05]",
    ghost: "text-primary-700 hover:bg-primary-500/10 dark:text-primary-300",
    danger: "bg-danger-500/10 text-danger-500 hover:bg-danger-500/15",
  };
  return cn(
    "inline-flex select-none items-center justify-center gap-2 rounded-full font-semibold transition-all duration-150 active:scale-[0.97]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
    "disabled:pointer-events-none disabled:opacity-50",
    sizes[size],
    variants[variant],
  );
}

export function getInitials(name: string) {
  return name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

/** Member dialogs: one shrinkable column (wide rows scroll instead of stretching), phone-safe size. */
export const memberDialogClass =
  "grid-cols-[minmax(0,1fr)] w-[calc(100%-2rem)] max-h-[90dvh] overflow-y-auto rounded-[24px] p-5 sm:p-6";
